import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";

import Breadcrums from "../components/breadcrums";
import { Link } from "react-router-dom";
import { getFilteredPayroll } from "../api/payroll-api";
import Moment from "react-moment";
import PayrollDrawer from "../components/payoutDrawer";
import { getEmployeeRecord, listOfEmployees } from "../api/consultants-api";
import CommonFilters from "../blocks/commonFilters";
import Currency from "react-currency-formatter";
import classNames from "classnames";
import { nanoid } from "nanoid";
import { useSnackbar } from "react-simple-snackbar";
import Skeleton from "react-loading-skeleton";
import PageTitle from "../components/pageTitle";
import { payrollBreadcrums } from "../constants/breadcrums";
import { alternativeBgClr, blueBgClr, button, buttonBase, payrollCommandActions } from "../constants/common";
import { ColumnDef } from "@tanstack/react-table";
import { PayrollType, consultant } from "../model";
import TanstackTable from "../components/TanstackTable";
import moment from "moment";
import CommonFiltersPayroll from "../blocks/commonFiltersPayroll";
// import PdfViewer from "../components/pdfViewer";

export interface LocalPayrollType {
    uuid: string;
    e_regular: number;
    e_earnings?: number; // Add this property if it is missing
    net_pay?: number; // Ensure this property is also defined if used elsewhere
    hrs?: number; // Ensure this property is also defined if used elsewhere
    check_date?: string; // For Excel export and table
    _d_start?: string; // For Excel export and table
    _d_end?: string; // For Excel export and table
}


export default function Payroll() {
    const [payrollList, setPayrollList] = useState<LocalPayrollType[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [people, setPeople] = useState<consultant[]>([]);
    const [activeStatus, setActiveStatus] = useState<"all" | "active" | "inactive">("all"); // New state for employee status
    const [checkDateFilter, setCheckDateFilter] = useState<"first-fortnight" | "second-fortnight" | "all">("all"); // Updated state for check date filter
    const [openSnackbar] = useSnackbar();

    const defaultStartDate = moment().subtract(0, "d").toDate();

    const toUTCFormat = (d: any) => {
        return moment.utc(moment(d)).format("MM/DD/YYYY");
    };

    // Download Excel functionality (now has access to all state and helpers)
    const handleDownloadExcel = () => {
        if (!payrollList || payrollList.length === 0) {
            openSnackbar("No payroll data to export.");
            return;
        }
        // Sort by name (A-Z), then by check_date (newest first)
        const sortedPayroll = [...payrollList].sort((a, b) => {
            const nameA = (getEmployeeRecord(people, a.uuid)?.name || a.uuid || "").toLowerCase();
            const nameB = (getEmployeeRecord(people, b.uuid)?.name || b.uuid || "").toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            // If names are equal, sort by check_date (newest first)
            const dateA = a.check_date ? new Date(a.check_date) : new Date(0);
            const dateB = b.check_date ? new Date(b.check_date) : new Date(0);
            return dateB.getTime() - dateA.getTime();
        });
        // Prepare data for export
        const exportData = sortedPayroll.map((payroll) => {
            const employee = getEmployeeRecord(people, payroll.uuid);
            return {
                Name: employee?.name || payroll.uuid,
                "Check Date": payroll.check_date ? toUTCFormat(payroll.check_date) : "",
                "Pay-Check Period": payroll._d_start && payroll._d_end ? `${toUTCFormat(payroll._d_start)} - ${toUTCFormat(payroll._d_end)}` : "",
                "Gross Pay (USD)": payroll.e_regular || 0,
                "Total Net Pay (USD)": payroll.net_pay || 0,
                Hours: payroll.hrs || 0,
            };
        });
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payroll");
        XLSX.writeFile(wb, `payroll_export_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    useEffect(() => {
        setLoading(true);
        setPayrollList([]);
        getFilteredPayroll({
            checkDate: defaultStartDate,
            employee: "",
            fortnightly: null
        })
            .then((data) => {
                console.log(data);
                if (data.error) {
                    openSnackbar(data.error ? data.error.sqlMessage : "");
                }
                setPayrollList(data || []);
            })
            .finally(() => {
                setLoading(false);
            });

        // Reset Data
        setPeople([]);
        listOfEmployees()
            .then((data) => setPeople(data))
            .finally(() => {
                setLoading(false);
            });
    }, []);

    function applyFilters(filters): any {
        console.log("checkDateFilter", checkDateFilter);
        console.log("filters", filters);

        filters.activeStatus = activeStatus; // FIXME

        getFilteredPayroll(filters).then((data) => {
            console.log(data);
            setPayrollList(data);
        });
    }

    return (
        <main>
            <Breadcrums breadcrumLinks={payrollBreadcrums} commands={payrollCommandActions} module={"Payroll"} groups={["Actions", "Statement"]} />

            <PageTitle title={"Payroll"} preSummary={"Organizational Overview"} summary={"Snapshot of Current Employee Roster and Vital Information."}>
                <div className="flex flex-row gap-2 items-center justify-end">
                    <Link to="/payroll/import">
                        <button type="button" className={classNames(button, buttonBase, alternativeBgClr, "px-2 mx-2")}> 
                            Import Payroll
                        </button>
                    </Link>
                    <Link to="/payroll/new">
                        <button type="button" className={classNames(button, buttonBase, blueBgClr)}>
                            New Payroll
                        </button>
                    </Link>
                    <button
                        type="button"
                        className={classNames(button, buttonBase, blueBgClr, "px-2 mx-2")}
                        onClick={handleDownloadExcel}
                    >
                        Download Excel
                    </button>
                </div>
            </PageTitle>

            <section className="px-4 py-0 sm:px-6 lg:px-8">
                {/* Dropdown to filter employees by status */}
                <div className="mb-4">
                    <label>Filter Employees:</label>
                    <select
                        onChange={(e) => setActiveStatus(e.target.value as "all" | "active" | "inactive")}
                        value={activeStatus}
                        className="block px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm w-96 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                        <option value="all">All Employees</option>
                        <option value="active">Active Employees</option>
                        <option value="inactive">Inactive Employees</option>
                    </select>
                </div>

                

                {/* Space between filters */}
                <div className="mb-6"></div>

                {/* Pass filtered employees to CommonFilters */}
                <CommonFiltersPayroll
                    dateLabel="Check Date"
                    people={people.filter((employee) => {
                        if (activeStatus === "active") return employee.active === 1;
                        if (activeStatus === "inactive") return employee.active === 0;
                        return true; // Show all employees for "all"
                    })}
                    applyFilters={applyFilters}
                ></CommonFiltersPayroll>
            </section>

            <section className="px-4 py-0 sm:px-6 lg:px-8">
                <div className="flex flex-col mt-8">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                            <div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg" style={{maxHeight: 'calc(100vh - 380px)', minHeight: '300px'}}>
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                Name
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Check Date
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Pay-Check Period
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Gross Pay <small className="text-xs font-medium text-gray-500">(USD)</small>
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Total Net Pay <small className="text-xs font-medium text-gray-500">(USD)</small>
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Hours
                                            </th>
                                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                <span className="sr-only">Edit</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {!isLoading && payrollList.length === 0 && (
                                            <tr>
                                                <td className="p-2 pl-4 text-sm font-normal text-gray-700 whitespace-nowrap sm:pl-6" colSpan={7}>
                                                    No records found
                                                </td>
                                            </tr>
                                        )}

                                        {isLoading && (
                                            <>
                                                <tr>
                                                    <td className="pl-4 pr-3 text-sm font-normal text-gray-900 whitespace-nowrap sm:pl-6">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="px-3 text-sm text-gray-500 whitespace-nowrap">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="px-3 text-sm text-gray-500 whitespace-nowrap">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="px-3 text-sm text-gray-500 whitespace-nowrap">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="px-3 text-sm text-gray-500 whitespace-nowrap">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="px-3 text-sm text-gray-500 whitespace-nowrap">
                                                        <Skeleton />
                                                    </td>
                                                    <td className="relative py-2 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-6">
                                                        <Skeleton />
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {payrollList.length > 0 &&
                                            [...payrollList]
                                                .filter((payroll) => payroll.e_regular && payroll.e_regular !== 0)
                                                .sort((a, b) => {
                                                    const nameA = (getEmployeeRecord(people, a.uuid)?.name || a.uuid || "").toLowerCase();
                                                    const nameB = (getEmployeeRecord(people, b.uuid)?.name || b.uuid || "").toLowerCase();
                                                    if (nameA < nameB) return -1;
                                                    if (nameA > nameB) return 1;
                                                    // If names are equal, sort by check_date (newest first)
                                                    const dateA = a.check_date ? new Date(a.check_date) : new Date(0);
                                                    const dateB = b.check_date ? new Date(b.check_date) : new Date(0);
                                                    return dateB.getTime() - dateA.getTime();
                                                })
                                                .map((payroll: any, payrollIdx) => (
                                                    <tr key={nanoid()} className={classNames(payrollIdx % 2 === 0 ? undefined : "bg-gray-50-x", " pl-4 pr-3 px-3 py-1.5 text-sm sm:pl-6 text-gray-900 hover:bg-gray-100")}> 
                                                        <td className="pl-4 font-normal whitespace-nowrap sm:pl-6"> {getEmployeeRecord(people, payroll.uuid)?.name || payroll.uuid}</td>
                                                        <td className="pl-4 whitespace-nowrap">
                                                            <Moment filter={toUTCFormat} local>
                                                                {payroll.check_date}
                                                            </Moment>
                                                        </td>
                                                        <td className="pl-4 whitespace-nowrap">
                                                            <Moment filter={toUTCFormat} local>
                                                                {payroll._d_start}
                                                            </Moment>{" "}
                                                            -{" "}
                                                            <Moment filter={toUTCFormat} local>
                                                                {payroll._d_end}
                                                            </Moment>
                                                        </td>
                                                        <td className="pl-4 text-sm font-normal text-left text-gray-900 whitespace-nowrap">
                                                            <Currency quantity={payroll.e_regular || 0} currency="USD" />
                                                        </td>
                                                        <td className="pl-4 text-sm font-normal text-left text-gray-900 whitespace-nowrap">
                                                            <Currency quantity={payroll.net_pay || 0} currency="USD" />
                                                        </td>
                                                        <td className="pl-4 text-sm font-normal text-left text-gray-900 whitespace-nowrap">{payroll.hrs}</td>
                                                        <td className="relative py-2 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-6">
                                                            <Link
                                                                to={`/payroll/${payroll.uuid}`}
                                                                preventScrollReset={true}
                                                                state={{
                                                                    checkDate: payroll.check_date,
                                                                    uuid: payroll.uuid,
                                                                    payroll: payroll,
                                                                }}
                                                                className="text-blue-600 no-underline hover:text-blue-900 no-line-through"
                                                            >
                                                                View<span className="sr-only">, {payroll.uuid}</span>
                                                            </Link>
                                                            &nbsp;
                                                            <Link
                                                                to={`/payroll/editor/${payroll.uuid}`}
                                                                preventScrollReset={true}
                                                                state={{
                                                                    checkDate: payroll.check_date,
                                                                    uuid: payroll.uuid,
                                                                    payroll: payroll,
                                                                }}
                                                                className="pl-3 text-blue-600 no-underline hover:text-blue-900 no-line-through"
                                                            >
                                                                Edit<span className="sr-only">, {payroll.uuid}</span>
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100 sticky bottom-0 z-10">
                                        {payrollList.length > 0 && (
                                            <tr>
                                                <td className="pl-4 pr-3 text-sm font-semibold text-gray-900 whitespace-nowrap sm:pl-6" colSpan={3}>
                                                    Total
                                                </td>
                                                <td className="pl-4 text-sm font-semibold text-left text-gray-900 whitespace-nowrap">
                                                    <Currency
                                                        quantity={payrollList.reduce((total, payroll) => total + (payroll.e_regular || 0), 0)}
                                                        currency="USD"
                                                    />
                                                </td>
                                                <td className="pl-4 text-sm font-semibold text-left text-gray-900 whitespace-nowrap">
                                                    <Currency
                                                        quantity={payrollList.reduce((total, payroll) => total + (payroll.net_pay || 0), 0)}
                                                        currency="USD"
                                                    />
                                                </td>
                                                <td className="pl-4 text-sm font-semibold text-left text-gray-900 whitespace-nowrap">
                                                    {payrollList.reduce((total, payroll) => total + (payroll.hrs || 0), 0).toLocaleString()}
                                                </td>
                                                <td className="relative py-2 pl-3 pr-4 text-sm font-medium text-right whitespace-nowrap sm:pr-6"></td>
                                            </tr>
                                        )}
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
